import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { generateId } from '../firebase/idGenerator';

function AdminRegister() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("support");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true); // Default to true for check
  const [vaultLocked, setVaultLocked] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const checkVault = async () => {
      try {
        const snap = await get(ref(db, 'settings/adminRegistrationEnabled'));
        if (snap.exists() && snap.val() === false) {
          setVaultLocked(true);
        }
      } catch (e) {
        console.error("Vault check failed:", e);
      }
      setLoading(false);
    };
    checkVault();
  }, []);



  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      return setError("Passwords do not match");
    }

    setLoading(true);

    try {
      // 1. Create Firebase Auth User
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Generate Unique IDs (AID and User UID + SUID)
      const newAdminId = await generateId('AID', 'admins', 'adminId');
      const newUid = await generateId('UID', 'users', 'uid');
      const newSuid = await generateId('SUID', 'users', 'suid');
      const newProfileId = await generateId('PID', 'users', 'profileId');

      const adminRecordStatus = 'pending';

      // 3. Save to dedicated /admins node
      const adminData = {
        adminId: newAdminId,
        uid: newUid,
        name: name,
        email: email,
        role: role,
        status: adminRecordStatus,
        createdAt: Date.now()
      };
      await set(ref(db, `admins/${user.uid}`), adminData);

      // 4. Auto-provision a public Developer Profile mapping to this admin
      const profileData = {
        uid: newUid,
        suid: newSuid,
        email: email,
        name: name,
        role: role, // Reflect status publicly if needed
        profileId: `DEV-${newProfileId.split('-')[2]}`, // Standard DEV format
        createdAt: Date.now()
      };
      await set(ref(db, `users/${user.uid}`), profileData);

      // Route to admin panel (Route Guard will intercept and block them if pending)
      navigate("/admin/dashboard");
      
    } catch (err) {
      setError(err.message || "Failed to register admin account.");
      console.error(err);
    }
    
    setLoading(false);
  };

  if (loading) return <div style={{color: '#00ffae', padding: '100px', textAlign: 'center'}}>Accessing Security Vault...</div>;

  if (vaultLocked) {
    return (
      <div className="container animate-up" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh'}}>
        <div className="glass-card" style={{ width: '100%', maxWidth: '500px', padding: '40px', borderTop: '4px solid #ff375f', textAlign: 'center' }}>
          <i className="fa-solid fa-vault" style={{fontSize: '3rem', color: '#ff375f', marginBottom: '20px'}}></i>
          <h2 style={{color: '#fff', marginBottom: '10px'}}>Security Vault Locked</h2>
          <p style={{color: '#8b949e', lineHeight: '1.6'}}>
            Public administrative registration is currently **DISABLED** for this sector. 
            Recruitment occurs only during authorized "Open Vault" periods configured by a Super Admin.
          </p>
          <div style={{marginTop: '30px', padding: '15px', background: 'rgba(255, 55, 95, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 55, 95, 0.1)'}}>
            <p style={{color: '#ff375f', fontSize: '0.8rem', margin: 0, fontWeight: 'bold'}}>SECURITY PROTOCOL: v2.7.1.0-RESTRCT</p>
          </div>
          <button onClick={() => navigate('/login')} className="btn btn-secondary" style={{width: '100%', marginTop: '30px'}}>
             Return to Secured Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container animate-up" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', padding: '40px 0'}}>
      <div className="glass-card" style={{ width: '100%', maxWidth: '500px', padding: '40px', borderTop: '4px solid #00ffae' }}>
        <div style={{textAlign: 'center', marginBottom: '30px'}}>
          <i className="fa-solid fa-shield-halved" style={{fontSize: '2rem', color: '#00ffae', marginBottom: '10px'}}></i>
          <h2 style={{color: '#fff', margin: 0}}>Admin Registration</h2>
          <p style={{color: '#8b949e', fontSize: '0.9rem', marginTop: '5px'}}>Create a new highly privileged DevTracker account.</p>
        </div>
        
        {error && <div className="error-text" style={{background: 'rgba(255, 107, 107, 0.1)', padding: '10px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center'}}>{error}</div>}
        
        <form onSubmit={handleRegister}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input 
              type="text" 
              className="form-input" 
              required
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Admin Email</label>
            <input 
              type="email" 
              className="form-input" 
              required
              value={email}
              autoComplete="username"
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          
          <div className="form-group" style={{marginBottom: '20px'}}>
            <label className="form-label">Permission Tier</label>
            <select 
              className="form-input" 
              value={role} 
              onChange={e => setRole(e.target.value)}
              style={{cursor: 'pointer'}}
            >
              <option value="superadmin">🔴 Superadmin (Full Access)</option>
              <option value="moderator">🟠 Moderator (Manage Users & Profiles)</option>
              <option value="support">🟢 Support (View Messages Only)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input" 
              required
              value={password}
              autoComplete="new-password"
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input 
              type="password" 
              className="form-input" 
              required
              value={confirmPassword}
              autoComplete="new-password"
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>

          <button disabled={loading} type="submit" className="btn btn-primary" style={{width: '100%', marginTop: '15px', background: '#00ffae', color: '#0d1117', fontWeight: 'bold'}}>
            {loading ? 'Provisioning Account...' : 'Generate Admin Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminRegister;
